import { BlockScrollStrategy, Overlay } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport
} from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { provideSvgIcons } from '@core/icons/svg-icons.provider';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { AppConfig } from '@core/token/app-config.token';
import { JwtInterceptor } from '@helpers/jwt.interceptor';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterStateSerializer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { JobListEffects } from '@pages/job-match/state/effects';
import { jobMatchReducer } from '@pages/job-match/state/reducers';
import { localStorageSyncReducer } from '@state/meta-reducers/localstorage-sync.reducer';
import { APP_REDUCERS } from '@state/reducers/app.reducer';
import { CustomSerializer } from '@state/router/custom-serializer';
import { AppConfigModel } from '@utils/interfaces/app-config.model';
import { provideApollo, provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from 'environment';
import { MarkdownModule, MARKED_OPTIONS, MarkedOptions, MarkedRenderer } from 'ngx-markdown';

import { APP } from './app.constant';
import { routes } from './app.routes';

const markedOptionsFactory = (): MarkedOptions => {
  const renderer = new MarkedRenderer();

  renderer.link = (href: string, title: string, text: string) =>
    href.includes('http')
      ? `<a title="${href}" href="${href}" target="_blank">${text}</a>`
      : `<a title="${href}" href="${href}">${text}</a>`;

  return {
    renderer
  };
};

const scrollFactory = (overlay: Overlay): BlockScrollStrategy => overlay.scrollStrategies.block();

export const setupAppConfig = (config: AppConfigModel): ApplicationConfig => {
  return {
    providers: [
      { provide: AppConfig, useValue: config },
      provideRouter(
        routes,
        withInMemoryScrolling({
          scrollPositionRestoration: 'disabled'
        }),
        withRouterConfig({
          paramsInheritanceStrategy: 'always'
        })
      ),
      provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
      provideAnimations(),
      provideStore(APP_REDUCERS, {
        metaReducers: [localStorageSyncReducer]
      }),
      provideRouterStore({ stateKey: 'router' }),
      !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],
      {
        provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
        useFactory: scrollFactory,
        deps: [Overlay]
      },
      { provide: RouterStateSerializer, useClass: CustomSerializer },
      { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
      importProvidersFrom(
        MarkdownModule.forRoot({
          markedOptions: {
            provide: MARKED_OPTIONS,
            useFactory: markedOptionsFactory
          }
        })
      ),
      provideApollo(() => {
        const httpLink = inject(HttpLink);
        const localStorageService = inject(LocalStorageService);

        const authHeader = setContext((_operation, _context) => {
          const jwt: string | null = localStorageService.getItem(APP.AUTH.INFOR);

          return { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} };
        });

        return {
          link: ApolloLink.from([authHeader, httpLink.create({ uri: config.graphQLAppGatewayUri })]),
          cache: new InMemoryCache()
        };
      }),
      provideNamedApollo(() => {
        const httpLink = inject(HttpLink);

        return {
          cpb: {
            // graphQLCPBUri points at the gateway CPB proxy, which attaches the
            // PathBuilder API key server-side. The key is never sent from the browser.
            link: httpLink.create({
              uri: config.graphQLCPBUri,
              withCredentials: false
            }),
            cache: new InMemoryCache()
          }
        };
      }),
      provideState('job-match', jobMatchReducer),
      provideEffects([JobListEffects]),
      provideSvgIcons
    ]
  };
};
